/* eslint-disable */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { makeStyles, RACCard, Typography, CircularProgress } from '@rentacenter/racstrap';
import clsx from 'clsx';
import { determineProductLeasability, determineImageLeasability, getClassifications, getPricingClassifications, getProductCatalogMatch, generateFunding, getRetailerLaunchOptions, getSpendLimitDetail } from '../../api/user';
import { removeAcimaItems, updateResponseToHaveStrings } from '../../utils/utils';
import { ReactComponent as ProcessSpinner } from '../../assets/images/Spinner.svg';
import { EligibilityDecision, EligibilityDecisionData } from './EligibilityDecision';
import { useNationalPartnerData, useNationalPartnerActions } from './NationalPartnerProvider';
import { validateItemLimits } from './validateItemLimits';
import { validatePrefundSLA, PrefundSLADecision } from './validatePrefundSLA';
import { PrefundSLAConfirmation } from './PrefundSLAConfirmation';
import { FundingApprovedPanel } from './FundingApprovedPanel';
import { PostCheckoutConfirmation } from './PostCheckoutConfirmation';
import { useVendorPopup } from './useVendorPopup';
import { deriveCartUrl, buildAgreementNavigationState, buildAgreementPath } from './navigationHelpers';
import { useHistory, useParams } from 'react-router-dom';

/**
 * Decodes a hidden steganographic message from an image.
 * The message is embedded in the LSB of R, G, B channels with a 4-byte big-endian length header.
 */
function decodeMessage(canvas: HTMLCanvasElement): string {
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imgData.data;

  let channelPos = 0;

  function readByte(): number {
    let byte = 0;
    for (let bit = 0; bit < 8; bit++) {
      const pixel = Math.floor(channelPos / 3);
      const channel = channelPos % 3;
      const dataIndex = pixel * 4 + channel;
      byte = (byte << 1) | (data[dataIndex] & 1);
      channelPos++;
    }
    return byte;
  }

  // Read 4-byte big-endian length header
  const length =
    ((readByte() << 24) >>> 0) |
    (readByte() << 16) |
    (readByte() << 8) |
    readByte();

  const capacityBytes = Math.floor((data.length / 4) * 3 / 8);
  if (length <= 0 || length > capacityBytes) {
    return '';
  }

  const bytes = new Uint8Array(length);
  for (let i = 0; i < length; i++) {
    bytes[i] = readByte();
  }

  const decoder = new TextDecoder();
  return decoder.decode(bytes);
}

/**
 * Decodes the hidden message from an image blob URL.
 */
function decodeFromImageUrl(imageUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(''); return; }
      ctx.drawImage(img, 0, 0);
      resolve(decodeMessage(canvas));
    };
    img.onerror = () => resolve('');
    img.src = imageUrl;
  });
}

/**
 * Converts an image blob URL to a base64 string (without the data:image/png;base64, prefix).
 *
 * NOTE: Logs the resulting PNG image size (bytes/MB) for investigation into the
 * Amazon/Walmart imageLeasable upload-failure issue. No compression/quality change is
 * applied here — this is purely instrumentation to capture real-world size data.
 */
function imageUrlToBase64(imageUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject('No canvas context'); return; }
      ctx.drawImage(img, 0, 0);
      const dataUrl = canvas.toDataURL('image/png');

      // Remove the "data:image/png;base64," prefix BEFORE measuring size —
      // the prefix is plain text, not base64 image data, and must be excluded.
      const base64 = dataUrl.split(',')[1];

      // Log the captured image's real byte size for size-investigation purposes
      // (no compression applied). Accounts for base64 padding ('=' / '==') so the
      // reported size matches the actual decoded byte length exactly.
      const paddingChars = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
      const actualImageBytes = Math.floor((base64.length * 3) / 4) - paddingChars;
      console.log(
        `[imageUrlToBase64] Captured PNG image size: ${actualImageBytes} bytes (~${(actualImageBytes / (1024 * 1024)).toFixed(2)} MB)`
      );

      resolve(base64);
    };
    img.onerror = () => reject('Failed to load image');
    img.src = imageUrl;
  });
}

/**
 * Generates a unique correlation ID.
 */
function generateCorrelationId(): string {
  return `LS-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

type EvaluationStatus = 'IDLE' | 'SUBMITTING' | 'SUCCESS' | 'FAILED' | 'TIMEOUT';

const STATUS_MESSAGES = [
  'Submitting image for evaluation...',
  'Analyzing cart items...',
  'Determining if items are leasable...',
  'Communicating with evaluation service...',
  'Awaiting response...',
  'Still processing, please wait...',
  'Image analysis in progress...',
  'Almost there...',
];

const TIMEOUT_MS = 90000; // 1 minutes — imageLeasable can take 60-90s for image processing

const useStyles = makeStyles((theme: any) => ({
  capturedImageRoot: {
    width: '100%',
    padding: '0.5rem 0',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    overflow: 'hidden',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  cardBody: {
    padding: '1rem 1.5rem',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column' as const,
  },
  contentRow: {
    display: 'flex',
    gap: '1.5rem',
    flex: 1,
    minHeight: 0,
  },
  leftPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FAFAFA',
    borderRadius: '8px',
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    padding: '1.5rem',
    position: 'relative' as const,
    minHeight: 0,
  },
  leftPanelPlaceholder: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '0.5rem',
    color: '#999',
  },
  leftPanelText: {
    fontSize: '0.875rem',
    color: '#666',
    textAlign: 'center' as const,
  },
  // Processing state
  processingContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    width: '100%',
  },
  spinnerIcon: {
    width: '60px',
    height: '60px',
  },
  statusMessage: {
    fontSize: '0.875rem',
    color: '#333',
    textAlign: 'center' as const,
    fontWeight: 500,
  },
  // Success state
  successContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    width: '100%',
  },
  successTitle: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: '#2E7D32',
    textAlign: 'center' as const,
  },
  successMessage: {
    fontSize: '0.875rem',
    color: '#333',
    textAlign: 'center' as const,
  },
  // Failed state
  failedContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    width: '100%',
  },
  failedTitle: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: '#C62828',
    textAlign: 'center' as const,
  },
  failedMessage: {
    fontSize: '0.875rem',
    color: '#333',
    textAlign: 'center' as const,
    lineHeight: 1.5,
  },
  retryButton: {
    marginTop: '0.75rem',
    padding: '8px 24px',
    backgroundColor: '#C62828',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.875rem',
    fontFamily: 'OpenSans-semibold',
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#B71C1C',
    },
  },
  // Submit button - kept for potential manual trigger
  // Right panel
  rightPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '1px solid #E0E0E0',
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    minHeight: 0,
  },
  rightPanelEmpty: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FAFAFA',
    border: 'none',
  },
  imageScrollContainer: {
    flex: 1,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#bbb',
      borderRadius: '4px',
      '&:hover': {
        background: '#888',
      },
    },
  },
  capturedImg: {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
  placeholder: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '0.5rem',
    color: '#999',
  },
  placeholderText: {
    fontSize: '0.875rem',
    color: '#666',
    textAlign: 'center' as const,
  },
  placeholderHint: {
    fontSize: '0.75rem',
    color: '#999',
  },
}));

export interface CapturedImageProps {
  triggerEvaluation?: boolean;
  onEvaluationStarted?: () => void;
  eligibilityData?: EligibilityDecisionData;
  onEligibilityUpdate?: (
    decision: 'NONE' | 'PROCESSING' | 'LEASABLE' | 'NOT_LEASABLE' | 'APPROVED' | 'CONDITIONAL' | 'DENIED',
    vendorName: string,
    totalCost: string,
    errorMessage?: string,
    classifications?: any
  ) => void;
}

export const CapturedImage = ({ triggerEvaluation, onEvaluationStarted, eligibilityData, onEligibilityUpdate }: CapturedImageProps) => {
  const classes = useStyles();
  const { vendorName: shopLocalVendorName, vendorCode, captureId, fundingState, spendLimitId, checkoutState, approvalLimitAmount, weeklyRate, totalCost, storeNumber, vendorId, supplierName, supplierOrderNumber, resolvedProducts, externalLaunchUrl , checkoutMessage, storePrefundMaxAmount, dmPrefundMaxAmount, slaApproved, configFetchFailed, enablePricingProductClassifications } = useNationalPartnerData();
  const { setIsProcessing, setFundingState, setSpendLimitId, calcTotalPricesWithFudgeFactor, setApprovalLimitAmount, setCheckoutState, setWeeklyRate, setTotalCost, setResolvedProducts, setStoreNumber, setVendorId, setSupplierName, setSupplierOrderNumber, setExternalLaunchUrl, resetExtendedState, setCheckoutMessage, setSlaApproved } = useNationalPartnerActions();

  // Router hooks for navigation
  const history = useHistory();
  const params = useParams<{ customerId?: string }>();

  // Local state for decline reason display
  const [declineReason, setDeclineReason] = useState<string>('');

  // Local state for navigation error messages
  const [navigationError, setNavigationError] = useState<string>('');

  // Local state for transaction amount from ramp-confirmation response
  const [transactionAmount, setTransactionAmount] = useState<string>('');

  // Local state for checkout error message
  const [checkoutError, setCheckoutError] = useState<string>('');

  // Prefund SLA gate state (RACV-308)
  const [pendingSlaValidation, setPendingSlaValidation] = useState<{
    decision: PrefundSLADecision;
    totalWithFudge: number;
    storeLimit: number | null;
    dmLimit: number | null;
    lineItems: Array<{ productName: string; quantity: string; unitCost: string }>;
    subtotal: number;
  } | null>(null);
  const slaConfirmResolveRef = useRef<(() => void) | null>(null);
  const slaConfirmRejectRef = useRef<((reason?: any) => void) | null>(null);

  // Vendor popup hook for checkout flow
  const { openPopup, isOpen: isPopupOpen, closePopup } = useVendorPopup();

  // Ref to store the last funding payload for retry
  const lastFundingPayloadRef = useRef<any>(null);
  const fundingAmountWithFudgeFactor = useRef<string>('0');

  // Store latest context values in refs so async callbacks always read current values
  const shopLocalVendorNameRef = useRef<string>(shopLocalVendorName);
  const shopLocalTotalCostRef = useRef<string>('');
  const shopLocalProductsRef = useRef<any[]>([]);
  const shopLocalWeeklyPaymentRef = useRef<string>('');

  // Keep SLA limit refs in sync — state updates may not be visible inside async callbacks
  const storePrefundMaxAmountRef = useRef<string | null>(storePrefundMaxAmount);
  const dmPrefundMaxAmountRef = useRef<string | null>(dmPrefundMaxAmount);
  storePrefundMaxAmountRef.current = storePrefundMaxAmount;
  dmPrefundMaxAmountRef.current = dmPrefundMaxAmount;

  // Keep feature flag ref in sync — async image processing callback may read stale closure value
  const enablePricingProductClassificationsRef = useRef<boolean>(enablePricingProductClassifications);
  enablePricingProductClassificationsRef.current = enablePricingProductClassifications;

  // Track previous popup state to detect close transition
  const prevIsPopupOpenRef = useRef<boolean>(false);

  // Detect popup close from CapturedImage's own popup instance (fallback)
  useEffect(() => {
    if (prevIsPopupOpenRef.current === true && isPopupOpen === false && checkoutState === 'IN_PROGRESS') {
      setCheckoutState('VERIFYING_PURCHASE');
    }
    prevIsPopupOpenRef.current = isPopupOpen;
  }, [isPopupOpen, checkoutState, setCheckoutState]);

  // When checkoutState becomes VERIFYING_PURCHASE, make a single call to /purchase-orders/ramp-confirmation.
  // If confirmed === true, transition to PURCHASE_SUCCESSFUL. Otherwise reset to NONE.
  useEffect(() => {
    if (checkoutState !== 'VERIFYING_PURCHASE') return;
    let cancelled = false;
    const wait = (ms: number) =>
      new Promise(resolve => setTimeout(resolve, ms));
    const verifyPurchase = async () => {
      const statusMessages = [
        'Verifying purchase...',
        'Still verifying, please wait...',
        'Final verification attempt...',
      ];

      try {
        for (let attempt = 0; attempt < 3; attempt++) {
          if (cancelled) return;
          // Update status message shown to user
         setCheckoutMessage(statusMessages[attempt]);
          const response = await getSpendLimitDetail(spendLimitId);
          if (cancelled) return;
          const data = response?.data;
          if (data?.confirmed === true) {
            setTransactionAmount(data?.usedAmount ?? '');
            setCheckoutState('PURCHASE_SUCCESSFUL');
            return;
          }
          if (attempt < 2) {
            await wait(5000);
            if (cancelled) return;
          }
        }
        // Keep final message visible for 5 seconds
        await wait(5000);
        // Failed after all 3 attempts
        if (cancelled) return;
        setCheckoutError(
          'Purchase was not confirmed. Please try again or contact support.'
        );
        setCheckoutState('CHECKOUT_FAILED');
      } catch (err: any) {
        if (cancelled) return;
        const errMsg =
          err?.response?.data?.message ||
          err?.message ||
          'An error occurred while verifying the purchase. Please try again.';
        setCheckoutError(errMsg);
        setCheckoutState('CHECKOUT_FAILED');
      }
    };

  verifyPurchase();

  return () => {
    cancelled = true;
  };
}, [checkoutState, spendLimitId, setCheckoutState]);

  // Fetch the retailer's externalLaunchUrl based on vendorCode for cart URL derivation
  useEffect(() => {
    if (!vendorCode) {
      setExternalLaunchUrl('');
      return;
    }
    let cancelled = false;
    const fetchUrl = async () => {
      try {
        const response = await getRetailerLaunchOptions();
        if (!cancelled && response?.status === 200 && response?.data) {
          const retailers = Array.isArray(response.data) ? response.data : [];
          const matched = retailers.find(
            (r: any) => r.retailerCode === vendorCode || r.retailerCode?.toUpperCase() === vendorCode?.toUpperCase()
          );
          if (matched?.externalLaunchUrl) {
            setExternalLaunchUrl(matched.externalLaunchUrl);
          }
        }
      } catch {
        // Silently fail — cart URL won't be derivable but won't break flow
      }
    };
    fetchUrl();
    return () => { cancelled = true; };
  }, [vendorCode]);

  // "Jump to Agreement" navigation handler
  // Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 5.7
  const handleJumpToAgreement = useCallback(() => {
    setNavigationError('');

    // Retrieve customerId from router params, falling back to sessionStorage
    const customerId = params.customerId || window.sessionStorage.getItem('customerId') || '';

    if (!customerId) {
      setNavigationError('Customer information is unavailable');
      return;
    }

    // Validate all fields via buildAgreementNavigationState
    const result = buildAgreementNavigationState({
      spendLimitId,
      storeNumber,
      vendorName: shopLocalVendorName,
      vendorId,
      supplierName,
      supplierOrderNumber,
      resolvedProducts,
    });

    if ('error' in result) {
      setNavigationError(result.error);
      return;
    }

    // All valid — navigate to Agreement_Screen
    history.push(buildAgreementPath(customerId, spendLimitId), result);
  }, [params.customerId, spendLimitId, storeNumber, shopLocalVendorName, vendorId, supplierName, supplierOrderNumber, resolvedProducts, history]);

  // Keep refs in sync on every render
  shopLocalVendorNameRef.current = shopLocalVendorName;

  // When vendor is selected via quick link — reset everything for a fresh capture
  // captureId increments on EVERY click (even same vendor) — always resets
  const vendorClickTimeRef = useRef<number>(0);
  const lastValidImageTimeRef = useRef<number>(0);
  const currentVendorCaptureIdRef = useRef<number>(0);
  const currentSessionImageHashRef = useRef<string>('');
  const lastProcessedClipboardHashRef = useRef<string>('');
  const lastCaptureTimeRef = useRef<number>(0);
  const altTPressedRef = useRef<boolean>(false); // Flag: Alt-T window is OPEN (expecting capture)
  const altTPressedTimeRef = useRef<number>(0); // When was Alt-T window opened?
  const altTExpectedTimeoutRef = useRef<any>(null); // NOT USED - keeping indefinitely open
  const lastCaptureWasExtensionRef = useRef<boolean>(false); // Track if last capture was via Alt-T
  const vendorWindowClosedTimeRef = useRef<number>(0); // Track when vendor window closed
  
  useEffect(() => {
    if (captureId > 0) {
      vendorClickTimeRef.current = Date.now();
      
      // CRITICAL: Open the Alt-T window INDEFINITELY
      // Keep it open until an image arrives
      // User has unlimited time to press Alt-T in the vendor popup
      altTPressedRef.current = true;
      altTPressedTimeRef.current = Date.now();
      
      // Clear any existing timeout
      if (altTExpectedTimeoutRef.current) {
        clearTimeout(altTExpectedTimeoutRef.current);
        altTExpectedTimeoutRef.current = null;
      }
      
      vendorWindowClosedTimeRef.current = 0; // Reset - vendor window not closed yet
      lastValidImageTimeRef.current = 0; // Reset — no valid image yet for this vendor
      currentSessionImageHashRef.current = ''; // Reset — clear previous session's image hash
      // NOTE: DO NOT reset lastProcessedClipboardHashRef - keep it persistent to detect stale clipboard images across all sessions
      currentVendorCaptureIdRef.current = captureId; // Mark which vendor session we're in
      // NOTE: DO NOT reset lastCaptureWasExtensionRef - keep it to track if LAST submission was Alt-T
      // This allows us to detect intentional retakes (when user presses Alt-T again with same image)
      
      // Clear previous image and evaluation state
      if (capturedImageUrl) {
        URL.revokeObjectURL(capturedImageUrl);
      }
      setCapturedImageUrl(null);
      setDecodedMessage('');
      setEvaluationStatus('IDLE');
      setEvaluationResult(null);
      setEvaluationError('');
      setFundingInProgress(false);
      setFundingTotal('');
      isProcessingRef.current = false;
      setReadyToCapture(true);

      // Reset all NationalPartnerProvider extended state (funding, checkout, etc.)
      // so that old overlays (Approved, Purchase Successful) are cleared
      resetExtendedState();
      setPendingSlaValidation(null);
      slaConfirmResolveRef.current = null;
      slaConfirmRejectRef.current = null;
      setDeclineReason('');
      setNavigationError('');

      if (onEligibilityUpdate) {
        onEligibilityUpdate('NONE', '', '');
      }
    }
  }, [captureId]);

  const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null);
  const [decodedMessage, setDecodedMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [evaluationStatus, setEvaluationStatus] = useState<EvaluationStatus>('IDLE');
  const [statusMessageIndex, setStatusMessageIndex] = useState<number>(0);
  const [evaluationResult, setEvaluationResult] = useState<any>(null);
  const [evaluationError, setEvaluationError] = useState<string>('');
  const [fundingInProgress, setFundingInProgress] = useState<boolean>(false);
  const [fundingTotal, setFundingTotal] = useState<string>('');
  const imageRef = useRef<HTMLImageElement | null>(null);
  const timeoutRef = useRef<any>(null);
  const statusIntervalRef = useRef<any>(null);
  const isProcessingRef = useRef<boolean>(false);
  // Only start capturing after user clicks a vendor quick link
  // Prevents auto-reading clipboard on page load/refresh
  const [readyToCapture, setReadyToCapture] = useState<boolean>(false);

  const processClipboardImage = useCallback(async (blob: Blob) => {
    const imageArrivalTime = Date.now();
    const timeSinceVendorClick = imageArrivalTime - vendorClickTimeRef.current;

    // CRITICAL: Only process images that arrive AFTER vendor was clicked
    if (vendorClickTimeRef.current === 0 || timeSinceVendorClick < 0) {
      isProcessingRef.current = false;
      return;
    }

    // CRITICAL: If we already captured an image in this session, don't process more clipboard images
    if (lastValidImageTimeRef.current > 0) {
      isProcessingRef.current = false;
      return;
    }

    // Calculate hash of the incoming image
    const arrayBuffer = await blob.arrayBuffer();
    const hashArray = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const imageHash = Array.from(new Uint8Array(hashArray))
      .map(b => b.toString(16).padStart(2, '0')).join('');

    // CRITICAL: Check if this is the same image from the previous session
    // RULE: Allow same-hash image ONLY if Alt-T window is OPEN (user is actively trying to retake)
    if (imageHash === lastProcessedClipboardHashRef.current) {
      if (altTPressedRef.current) {
        // Alt-T window is OPEN → user intentionally retaking → ALLOW
      } else {
        // Alt-T window is CLOSED → stale image → BLOCK
        const url = URL.createObjectURL(blob);
        URL.revokeObjectURL(url);
        isProcessingRef.current = false;
        return;
      }
    }

    // NOTE: Triple-layer defense against old images:
    // 1. lastValidImageTimeRef check (prevents reprocessing within same session)
    // 2. lastProcessedClipboardHashRef check (persists across sessions, blocks same image from being reprocessed)
    // 3. vendorClickTimeRef check (only processes after vendor is clicked)

    const url = URL.createObjectURL(blob);
    // Decode first to check if it's a valid RAC image
    const message = await decodeFromImageUrl(url);
    if (message === 'RAC') {
      // Update tracking - PERSIST the hash across all sessions
      currentSessionImageHashRef.current = imageHash;
      // NOTE: DO NOT update lastProcessedClipboardHashRef yet - only update after successful submission
      lastValidImageTimeRef.current = imageArrivalTime;
      
      // Valid RAC image — replace the current one
      if (capturedImageUrl) {
        URL.revokeObjectURL(capturedImageUrl);
      }
      setCapturedImageUrl(url);
      setDecodedMessage(message);
      
      // CRITICAL: Only auto-trigger evaluation if Alt-T window is OPEN
      // This means user clicked vendor and is waiting to capture
      // Alt-T is the ONLY way to capture images in vendor popup
      if (altTPressedRef.current) {
        console.log('[processClipboardImage]  Alt-T window OPEN AND valid RAC image - auto-submitting');
        // Mark that we got the Alt-T capture
        lastCaptureWasExtensionRef.current = true;
        // Close the Alt-T window immediately since we got the image
        altTPressedRef.current = false;
        if (altTExpectedTimeoutRef.current) {
          clearTimeout(altTExpectedTimeoutRef.current);
          altTExpectedTimeoutRef.current = null;
        }
        // Auto-trigger leasability evaluation immediately on image capture (Alt-T)
        setTimeout(() => {
          handleSubmitImageFromUrl(url);
          // After submission, update hash to mark this image as "processed"
          lastProcessedClipboardHashRef.current = imageHash;
        }, 100);
      } else {
        console.log('[processClipboardImage]  Alt-T window is CLOSED - NOT auto-submitting (image displayed only)');
        lastCaptureWasExtensionRef.current = false;
        isProcessingRef.current = false;
      }
    } else {
      URL.revokeObjectURL(url);
      isProcessingRef.current = false;
    }
  }, [capturedImageUrl]);

  useEffect(() => {
    // Listen for the Chrome extension to send the captured image
    // Extension dispatches a CustomEvent 'rac-image-captured' with image data after Alt+T
    // OR sends via window.postMessage with type 'RAC_IMAGE_CAPTURED'
    // NO clipboard access — extension communicates directly with the page
    const handleExtensionCapture = async (e: any) => {
      if (!readyToCapture) {
        return;
      }
      if (isProcessingRef.current) {
        return;
      }
      const detail = e.detail || {};
      const imageData = detail.imageBase64 || detail.dataUrl || '';
      if (!imageData) {
        return;
      }

      isProcessingRef.current = true;
      lastCaptureWasExtensionRef.current = true; // Mark this as Alt-T capture
      let blob: Blob;
      if (imageData.startsWith('data:')) {
        const res = await fetch(imageData);
        blob = await res.blob();
      } else {
        const byteString = atob(imageData);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        blob = new Blob([ab], { type: 'image/png' });
      }
      await processClipboardImage(blob);
    };

    const handlePostMessage = async (e: MessageEvent) => {
      if (e.data?.type === 'RAC_IMAGE_CAPTURED' && e.data?.imageData) {
        if (!readyToCapture) {
          return;
        }
        if (isProcessingRef.current) {
          return;
        }
        isProcessingRef.current = true;
        const imageData = e.data.imageData;
        lastCaptureWasExtensionRef.current = true; // Mark this as Alt-T capture
        
        let blob: Blob;
        if (imageData.startsWith('data:')) {
          const res = await fetch(imageData);
          blob = await res.blob();
        } else {
          const byteString = atob(imageData);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          blob = new Blob([ab], { type: 'image/png' });
        }
        await processClipboardImage(blob);
      }
    };

    // Paste as fallback for testing only — requires user to explicitly paste
    const handlePaste = (e: ClipboardEvent) => {
      if (!readyToCapture) return;
      if (isProcessingRef.current) return;
      const items = e.clipboardData?.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.startsWith('image/')) {
            const blob = items[i].getAsFile();
            if (blob) {
              isProcessingRef.current = true;
              processClipboardImage(blob);
            }
          }
        }
      }
    };

    // CRITICAL: Detect when vendor popup window closes (window regains focus from popup)
    // Just try to read clipboard — the altTPressedRef flag determines if we auto-submit
    const handleWindowFocus = async () => {
      if (!readyToCapture) return;
      if (isProcessingRef.current) return;
      
      if (vendorClickTimeRef.current === 0) {
        return;
      }
      
      if (lastValidImageTimeRef.current > 0) {
        // Already captured an image this session
        return;
      }
      
      try {
        const clipboardItems = await navigator.clipboard.read();
        for (const item of clipboardItems) {
          const imageType = item.types.find((type: string) => type.startsWith('image/'));
          if (imageType) {
            const blob = await item.getType(imageType);
            isProcessingRef.current = true;
            await processClipboardImage(blob);
            return;
          }
        }
      } catch (e: any) {
      }
    };

    window.addEventListener('rac-image-captured', handleExtensionCapture);
    window.addEventListener('message', handlePostMessage);
    document.addEventListener('paste', handlePaste);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('rac-image-captured', handleExtensionCapture);
      window.removeEventListener('message', handlePostMessage);
      document.removeEventListener('paste', handlePaste);
      window.removeEventListener('focus', handleWindowFocus);
      if (altTExpectedTimeoutRef.current) {
        clearTimeout(altTExpectedTimeoutRef.current);
      }
    };
  }, [processClipboardImage, readyToCapture]);

  useEffect(() => {
    return () => {
      if (capturedImageUrl) URL.revokeObjectURL(capturedImageUrl);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
    };
  }, [capturedImageUrl]);

  // Auto-trigger evaluation when Confirm Request is clicked
  const isValidCapture = decodedMessage === 'RAC';

  useEffect(() => {
    if (triggerEvaluation) {
      if (capturedImageUrl && isValidCapture) {
        handleSubmitImage();
      }
      if (onEvaluationStarted) onEvaluationStarted();
    }
  }, [triggerEvaluation]);

  /**
   * Submit the captured image for eligibility evaluation.
   * Accepts an explicit imageUrl param so it can be called immediately after
   * setCapturedImageUrl (before React state has flushed).
   * 
   * CRITICAL: Only submits if:
   * 1. Alt-T was pressed (extension handler fired)
   * 2. Image arrived AFTER vendor was clicked
   * 3. Valid RAC image is present
   */
  const handleSubmitImageFromUrl = async (imageUrl: string) => {
    if (!imageUrl) {
      return;
    }

    // CRITICAL: Only submit if Alt-T was the source (extension handler fired)
    if (!lastCaptureWasExtensionRef.current) {
      isProcessingRef.current = false;
      return;
    }

    // CRITICAL: Reject if no valid image has arrived since vendor click
    if (lastValidImageTimeRef.current === 0) {
      isProcessingRef.current = false;
      // CRITICAL: Clear the stale image from state so it can never be resubmitted
      setCapturedImageUrl(null);
      setDecodedMessage('');
      return;
    }

    // CRITICAL: Verify the image arrived AFTER vendor was clicked
    const timeSinceVendorClick = lastValidImageTimeRef.current - vendorClickTimeRef.current;
    
    if (timeSinceVendorClick <= 0) {
      // Image timestamp is before or equal to vendor click time - it's an old image
      isProcessingRef.current = false;
      // CRITICAL: Clear the stale image from state
      setCapturedImageUrl(null);
      setDecodedMessage('');
      return;
    }

    // Disable vendor buttons during processing
    setIsProcessing(true);
    
    // Clear clipboard immediately after API call is triggered
    try {
      await navigator.clipboard.writeText('');
    } catch (err) {
      // Clipboard clear failed - continue anyway
    }
    
    // Reset state
    setEvaluationStatus('SUBMITTING');
    setStatusMessageIndex(0);
    setEvaluationResult(null);
    setEvaluationError('');
    setFundingInProgress(false);

    if (onEligibilityUpdate) {
      onEligibilityUpdate('PROCESSING', '', '');
    }

    let msgIndex = 0;
    statusIntervalRef.current = setInterval(() => {
      msgIndex = (msgIndex + 1) % STATUS_MESSAGES.length;
      setStatusMessageIndex(msgIndex);
    }, 4000);

    try {
      const imageBase64 = await imageUrlToBase64(imageUrl);
      const payload = {
        merchantId: 0,
        correlationId: generateCorrelationId(),
        imageBase64: imageBase64,
      };

      // Log the full outgoing payload size for size-investigation purposes
      const payloadBytes = new Blob([JSON.stringify(payload)]).size;
      console.log(
        `[handleSubmitImageFromUrl] Outgoing imageLeasable payload size: ~${payloadBytes} bytes (~${(payloadBytes / (1024 * 1024)).toFixed(2)} MB)`
      );

      // Call the determine-leasability endpoint (image-based)
      const response = await determineImageLeasability(payload);

      // Clear timers
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);

      // Handle response — API may return an array or object
      if (response && Number(response.status) === 200) {
        const responseData = Array.isArray(response.data) ? response.data[0] : response.data;
        const items = responseData?.items || [];

        const leasableItems = items.filter((item: any) => String(item?.leaseable) === '1');
        const notLeasableItems = items.filter((item: any) => String(item?.leaseable) !== '1');
        const allLeasable = items.length > 0 && notLeasableItems.length === 0;
        const noneLeasable = leasableItems.length === 0;

        setEvaluationResult({
          leasable: allLeasable,
          message: responseData?.mesage || '',
          items: items,
          leasableItems: leasableItems,
          notLeasableItems: notLeasableItems,
        });
        setEvaluationStatus('SUCCESS');

        if (onEligibilityUpdate) {

          const purchaseTotal = responseData?.total ?? responseData?.subtotal;
          const purchaseTotalText = String(purchaseTotal ?? '').trim().replace(/^\$\s*/, '');
          if (
            (typeof purchaseTotal !== 'string' && typeof purchaseTotal !== 'number') ||
            !/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?$/.test(purchaseTotalText)
          ) {
            throw new Error('Unable to calculate funding. Please capture a valid purchase total and try again.');
          }
          const purchaseAmount = Number(purchaseTotalText.replace(/,/g, ''));
          if (!Number.isFinite(purchaseAmount) || purchaseAmount < 0) {
            throw new Error('Unable to calculate funding. The purchase total must be a valid nonnegative amount.');
          }
          const calculatedFunding = calcTotalPricesWithFudgeFactor(purchaseAmount);
          if (!Number.isFinite(Number(calculatedFunding)) || Number(calculatedFunding) < 0) {
            throw new Error('Unable to calculate funding. The calculated funding amount is invalid.');
          }
          fundingAmountWithFudgeFactor.current = calculatedFunding;
          
          const displayTotal = fundingAmountWithFudgeFactor.current || '';
          
          // Validate item limits using the utility before proceeding to pricing
          const itemLimitResult = validateItemLimits(leasableItems);
          if (!itemLimitResult.valid) {
            onEligibilityUpdate('DENIED', '', displayTotal, itemLimitResult.errorMessage);
            setIsProcessing(false);
            return;
          }

          if (allLeasable) {
            onEligibilityUpdate('LEASABLE', '', displayTotal);

            // ─── Prefund SLA validation gate (RACV-308) ────────────────────────────
            // Fires IMMEDIATELY after imageLeasable returns total cost (with fudge factor).
            // Compares fudged total against store/DM limits from config service.
            // Uses refs to get latest config values (state may not have re-rendered yet).
            const totalWithFudgeForSla = Number(fundingAmountWithFudgeFactor.current) || 0;
            const storeLimitForSla = storePrefundMaxAmountRef.current !== null && storePrefundMaxAmountRef.current !== ''
              ? Number(storePrefundMaxAmountRef.current) : null;
            const dmLimitForSla = dmPrefundMaxAmountRef.current !== null && dmPrefundMaxAmountRef.current !== ''
              ? Number(dmPrefundMaxAmountRef.current) : null;
            const slaDecision = validatePrefundSLA(totalWithFudgeForSla, storeLimitForSla, dmLimitForSla);

            if (slaDecision.decision !== 'WITHIN_STORE_LIMIT') {
              // Build line items from imageLeasable response for SLA panel display
              const slaLineItems = leasableItems.map((item: any) => ({
                productName: String(item.description || ''),
                quantity: String(item.quantity || '1'),
                unitCost: String(item.price || '0'),
              }));
              const slaSubtotal = leasableItems.reduce(
                (sum: number, item: any) => sum + (parseFloat(item.price || '0') * parseInt(item.quantity || '1', 10)), 0);

              setIsProcessing(false);
              setPendingSlaValidation({
                decision: slaDecision.decision,
                totalWithFudge: slaDecision.totalWithFudge,
                storeLimit: slaDecision.storeLimit,
                dmLimit: slaDecision.dmLimit,
                lineItems: slaLineItems,
                subtotal: slaSubtotal,
              });

              if (slaDecision.decision === 'EXCEEDS_DM_LIMIT' || slaDecision.decision === 'CONFIG_UNAVAILABLE') {
                // Hard block — flow stops here
                return;
              }

              // REQUIRES_MANAGER_PIN — pause here until the manager enters their PIN.
              // Loop so the user can cancel and re-enter without restarting the capture flow.
              let pinApproved = false;
              while (!pinApproved) {
                try {
                  await new Promise<void>((resolve, reject) => {
                    slaConfirmResolveRef.current = resolve;
                    slaConfirmRejectRef.current = reject;
                  });
                  pinApproved = true;
                } catch (e: any) {
                  if (e?.message === 'SLA_CANCELLED') {
                    // User cancelled PIN — keep pendingSlaValidation visible so the
                    // panel shows the amber banner and "Re-enter Manager PIN" link.
                    // Loop back: next iteration arms a fresh promise automatically.
                    continue;
                  }
                  // Unexpected error — hard abort.
                  slaConfirmResolveRef.current = null;
                  slaConfirmRejectRef.current = null;
                  setIsProcessing(false);
                  return;
                }
              }
              setPendingSlaValidation(null);
              slaConfirmRejectRef.current = null;
              setIsProcessing(true);
            }
            // WITHIN_STORE_LIMIT or PIN-approved proceeds to existing flow below

            // Expand items by quantity — if quantity > 1, duplicate the item as separate products
            // Each unit becomes its own line (quantity 1) through the entire flow
            const expandedItems: any[] = [];
            leasableItems.forEach((item: any) => {
              const qty = parseInt(item.quantity || '1', 10);
              for (let i = 0; i < qty; i++) {
                expandedItems.push({ ...item, quantity: 1 });
              }
            });

            const itemsWithModel = expandedItems.filter((item: any) => item.modelNumber || item.rmsItemNumber || item.sku);
            const itemsWithoutModel = expandedItems.filter((item: any) => !item.modelNumber && !item.rmsItemNumber && !item.sku);

            // Keep "Item is Leasable" visible for 3 seconds before moving to pricing
            await new Promise(resolve => setTimeout(resolve, 4000));
            onEligibilityUpdate('PROCESSING', '', displayTotal, 'Retrieving pricing information...');

            let classifications: any[] = [];

            const autoPricedProducts: any[] = [];
            if (itemsWithModel.length > 0) {
              try {
                const storeNumber = window.sessionStorage.getItem('storeNumber') || '';

                const catalogResponse = await getProductCatalogMatch({
                  storeNumbers: [storeNumber],
                  items: itemsWithModel.map((item: any) => ({
                    modelNumber: String(item.modelNumber || item.sku || ''),
                    purchaseCost: item.price || '0',
                    productDescription: item.description || '',
                    rmsItemNumber: item.rmsItemNumber || '',
                    manufacturerPartNumber: item.sku || item.modelNumber || '',
                    supplierName: (item.description || '').split(/\s*[-–]\s*/)[0] || '',
                  })),
                });
                const responseData = catalogResponse?.data || catalogResponse;
                const pricedItems = responseData?.items || [];
                
                console.log('[LocalSourcing] ProductCatalogMatch raw response:', JSON.stringify(responseData, null, 2));
                
                // Match response items to request items
                // Since items may be duplicated (quantity expansion), multiple request items 
                // can share the same purchaseCost. We match each request item to a response item
                // by purchaseCost — the same response item can match multiple request items.
                itemsWithModel.forEach((item: any) => {
                  const itemCost = item.price || '0';
                  const matchedPriced = pricedItems.find((p: any) => String(p.purchaseCost) === String(itemCost));
                  
                  console.log('[LocalSourcing] Matching item cost:', itemCost, '→ matched:', matchedPriced ? 'YES' : 'NO');
                  
                  if (matchedPriced?.rateTerm?.weeklyRate && matchedPriced?.rateTerm?.totalCost) {
                    const autoPricedItem = {
                      productName: item.description || '',
                      unitCost: itemCost,
                      quantity: String(item.quantity || '1'),
                      autoPriced: true,
                      rmsItemNumber: matchedPriced.rmsItemNumber ? String(matchedPriced.rmsItemNumber) : '',
                      weeklyRate: matchedPriced.rateTerm.weeklyRate || '',
                      weeklyTerm: matchedPriced.rateTerm.weeklyTerm ? String(matchedPriced.rateTerm.weeklyTerm) : '',
                      totalCost: matchedPriced.rateTerm.totalCost || '',
                      cashPrice: matchedPriced.rateTerm.cashPrice || matchedPriced.rateTerm.totalCost || '',
                      modelNumber: item.modelNumber || item.sku || '',
                      model: matchedPriced.modelNumber || item.description || '',
                      brand: matchedPriced.brand || '',
                      costPerItem: itemCost,
                    };
                    console.log('[LocalSourcing] Auto-priced product built:', JSON.stringify(autoPricedItem, null, 2));
                    autoPricedProducts.push(autoPricedItem);
                  } else {
                    // No match found for this item — treat as manual
                    autoPricedProducts.push({
                      productName: item.description || '',
                      unitCost: itemCost,
                      quantity: String(item.quantity || '1'),
                      autoPriced: false,
                      modelNumber: item.modelNumber || item.sku || '',
                    });
                  }
                });
              } catch {
                itemsWithModel.forEach((item: any) => {
                  autoPricedProducts.push({ productName: item.description || '', unitCost: item.price || '0', quantity: String(item.quantity || '1'), autoPriced: false, modelNumber: item.modelNumber || item.sku || '' });
                });
              }
            }

            const manualProducts = itemsWithoutModel.map((item: any) => ({
              productName: item.description || '', unitCost: item.price || '0', quantity: String(item.quantity || '1'), autoPriced: false, modelNumber: item.modelNumber || item.sku || '',
            }));

            const allProducts = [...autoPricedProducts, ...manualProducts];

            // Only call getClassifications if there are items that need manual pricing dropdowns
            // (items without model OR items that failed auto-pricing)
            const needsManualPricing = allProducts.some((p: any) => !p.autoPriced);
            if (needsManualPricing) {
              try {
                // Use new RACAPI (POST /classification) if flag is enabled, otherwise use old enterprise (GET /classifications/processed)
                const storeNum = window.sessionStorage.getItem('storeNumber') || '';
                let classData: any;
                // Read from ref to get the latest value — the closure captures the stale initial value
                const flagValue = enablePricingProductClassificationsRef.current;
                console.log('[LocalSourcing] enablePricingProductClassifications flag value:', flagValue);
                if (flagValue) {
                  classData = await getPricingClassifications({ storeNumber: [storeNum] });
                  // New RACAPI returns numbers for IDs and includes ACIMA entries — need to clean up
                  const classificationsByDepts = classData?.data?.classificationsByDepts || classData?.classificationsByDepts || [];
                  removeAcimaItems(classificationsByDepts);
                  updateResponseToHaveStrings(classificationsByDepts);
                  classifications = classificationsByDepts;
                  console.log('[LocalSourcing] getPricingClassifications (RACAPI) called — ACIMA removed, IDs converted to strings');
                } else {
                  classData = await getClassifications();
                  const classificationsByDepts = classData?.data?.classificationsByDepts || classData?.classificationsByDepts || [];
                  removeAcimaItems(classificationsByDepts);
                  classifications = classificationsByDepts;
                  console.log('[LocalSourcing] getClassifications (enterprise) called — ACIMA removed');
                }
                console.log('[LocalSourcing] Classifications loaded — manual pricing needed for', allProducts.filter((p: any) => !p.autoPriced).length, 'items');
              } catch (classErr) { /* continue without classifications */ }
            } else {
              console.log('[LocalSourcing] Skipping getClassifications — all items auto-priced successfully');
            }

            onEligibilityUpdate('CONDITIONAL', '', displayTotal, undefined, {
              classifications,
              products: allProducts,
              weeklyRate: '',
            });

          } else if (noneLeasable) {
            onEligibilityUpdate('NOT_LEASABLE', '', displayTotal, undefined, notLeasableItems);
          } else {
            onEligibilityUpdate('NOT_LEASABLE', '', displayTotal, undefined, notLeasableItems);
          }
          
          // Reset the Alt-T flag after successful submission
          // This prepares for the next capture (user must press Alt-T again for next retake)
          lastCaptureWasExtensionRef.current = false;
          setIsProcessing(false);
        }
      } else {
        const errorData = Array.isArray(response?.data) ? response.data[0] : response?.data;
        const errorMsg = errorData?.mesage || errorData?.message || errorData?.errors?.[0]?.message || '';
        const stackTrace = errorData?.stackTrace || errorData?.stacktrace || '';
        const httpStatus = response?.status || response?.response?.status;

        // Detect 413 - image too large
        if (httpStatus === 413) {
          setEvaluationStatus('FAILED');
          setEvaluationError('Image is too large. Please capture a smaller or cropped screenshot and try again.');
        // Detect timeout from backend error response
        } else if (errorMsg.toLowerCase().includes('timeout')
          || stackTrace.toLowerCase().includes('timeout')
          || stackTrace.includes('ECONNABORTED')) {
          setEvaluationStatus('TIMEOUT');
          setEvaluationError('This is taking longer than expected. Please try again or contact support.');
        } else {
          setEvaluationStatus('FAILED');
          setEvaluationError(errorMsg || 'Unable to determine item eligibility. Please try again.');
        }
        setIsProcessing(false);
        if (onEligibilityUpdate) onEligibilityUpdate('NONE', '', '');
      }
    } catch (err: any) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);

      // Detect timeout from backend response (ECONNABORTED or timeout message)
      const errorMessage = err?.message || err?.data?.message || '';
      const errorCode = err?.code || err?.data?.code || '';
      const isTimeout = errorCode === 'ECONNABORTED'
        || errorMessage.toLowerCase().includes('timeout')
        || errorMessage.toLowerCase().includes('timed out');

      if (isTimeout) {
        setEvaluationStatus('TIMEOUT');
        setEvaluationError('This is taking longer than expected. Please try again or contact support.');
      } else {
        setEvaluationStatus('FAILED');
        setEvaluationError(errorMessage || 'An unexpected error occurred. Please try again.');
      }
      setIsProcessing(false);
      if (onEligibilityUpdate) onEligibilityUpdate('NONE', '', '');
    }
  };

  /**
   * Submit the captured image for eligibility evaluation.
   * CRITICAL: Only works if a valid image was captured in the current vendor session.
   */
  const handleSubmitImage = async () => {
    if (!capturedImageUrl) return;
    
    // CRITICAL: Only submit if we have a valid image from CURRENT vendor session
    // If lastValidImageTimeRef is 0, no image has been captured since vendor was clicked
    if (lastValidImageTimeRef.current === 0) {
      console.warn('No valid image captured in current session - blocking submission');
      return;
    }
    
    await handleSubmitImageFromUrl(capturedImageUrl);
  };

  const handleRetry = () => {
    setEvaluationStatus('IDLE');
    setEvaluationError('');
    setEvaluationResult(null);
    isProcessingRef.current = false; // Unlock so new capture can happen
    // Re-trigger the image submission immediately if we have a valid capture
    if (capturedImageUrl && decodedMessage === 'RAC') {
      handleSubmitImage();
    }
  };

  /**
   * Retry Funding: re-call generateFunding with the same pricing payload,
   * reset fundingState to POLLING on STARTED response.
   */
  const handleRetryFunding = async () => {
    if (!lastFundingPayloadRef.current) return;

    setFundingState('IDLE');
    setDeclineReason('');

    try {
      const fundingResponse = await generateFunding(lastFundingPayloadRef.current);
      const fundingData = fundingResponse?.data || fundingResponse;
      const fundingStatus = (fundingData?.status || '').toUpperCase();

      if (fundingStatus === 'STARTED' || fundingStatus === 'PENDING') {
        setFundingState('TIMED_OUT');
        setDeclineReason('Funding is still being processed by Ramp. Please retry in a few moments.');
      } else if (fundingStatus === 'APPROVED' || fundingStatus === 'SUCCESS') {
        setFundingState('APPROVED');
        setApprovalLimitAmount(fundingData?.approvalLimitAmount || '');
      } else {
        setFundingState('DECLINED');
        setDeclineReason(fundingData?.message || 'Funding request was declined.');
      }
    } catch (err: any) {
      setFundingState('CONNECTION_ERROR');
    }
  };

  /**
   * Start Over: clear captured image, reset evaluation state, reset all
   * NationalPartnerProvider extended state, and return Left_Panel to idle.
   */
  const handleStartOver = () => {
    // Clear captured image
    if (capturedImageUrl) {
      URL.revokeObjectURL(capturedImageUrl);
    }
    setCapturedImageUrl(null);
    setDecodedMessage('');

    // Reset evaluation state
    setEvaluationStatus('IDLE');
    setEvaluationResult(null);
    setEvaluationError('');

    // Reset funding progress
    setFundingInProgress(false);
    setFundingTotal('');

    // Reset isProcessing
    setIsProcessing(false);
    isProcessingRef.current = false;

    // Reset all NationalPartnerProvider extended state
    resetExtendedState();

    // Reset decline reason
    setDeclineReason('');

    // Return Left_Panel to idle placeholder
    if (onEligibilityUpdate) {
      onEligibilityUpdate('NONE', '', '');
    }
  };

  /**
   * Cancel: stop polling, discard funding state, reset isProcessing,
   * and return to eligibility decision screen.
   */
  const handleCancel = () => {
    // Reset isProcessing
    setIsProcessing(false);
    isProcessingRef.current = false;

    // Reset funding state
    resetExtendedState();
    setFundingInProgress(false);
    setFundingTotal('');
    setDeclineReason('');

    // Return to idle
    if (onEligibilityUpdate) {
      onEligibilityUpdate('NONE', '', '');
    }
  };

  // Render left panel content based on evaluation status
  const renderLeftPanel = () => {
    // Prefund SLA gate panel (RACV-308) — takes priority when active
    if (pendingSlaValidation) {
      return (
        <PrefundSLAConfirmation
          lineItems={pendingSlaValidation.lineItems}
          subtotal={pendingSlaValidation.subtotal}
          fudgeAmount={pendingSlaValidation.totalWithFudge - pendingSlaValidation.subtotal}
          variancePercent={0}
          totalWithFudge={pendingSlaValidation.totalWithFudge}
          storeLimit={pendingSlaValidation.storeLimit}
          dmLimit={pendingSlaValidation.dmLimit}
          decision={pendingSlaValidation.decision}
          slaApproved={slaApproved}
          currentRole={window.localStorage.getItem('currentRole') || ''}
          onConfirmRequest={() => {}}
          onSlaApproved={() => {
            setSlaApproved(true);
            if (slaConfirmResolveRef.current) {
              const resolve = slaConfirmResolveRef.current;
              slaConfirmResolveRef.current = null;
              resolve();
            }
          }}
          onSlaCancelled={() => {
            // Reject current promise → while-loop catch fires with SLA_CANCELLED
            // → loop continues → next iteration re-arms a fresh promise.
            if (slaConfirmRejectRef.current) {
              const reject = slaConfirmRejectRef.current;
              slaConfirmRejectRef.current = null;
              slaConfirmResolveRef.current = null;
              reject(new Error('SLA_CANCELLED'));
            }
          }}
        />
      );
    }

    // Show processing spinner while submitting leasability (takes priority)
    if (evaluationStatus === 'SUBMITTING') {
      return (
        <div className={classes.processingContainer}>
          <ProcessSpinner className={classes.spinnerIcon} />
          <Typography className={classes.statusMessage}>
            {STATUS_MESSAGES[statusMessageIndex]}
          </Typography>
        </div>
      );
    }

    // If eligibilityData is provided with a decision, show EligibilityDecision component
    if (eligibilityData && eligibilityData.decision !== 'NONE') {
      return <EligibilityDecision data={eligibilityData} onPricingResolved={async (pricing) => {
        // Use refs so we always read the latest vendorName/totalCost/products,
        // not the stale closure value from when EligibilityDecision first mounted
        const currentVendorName = shopLocalVendorNameRef.current;
        const currentTotalCost = shopLocalTotalCostRef.current;
        const currentProducts = shopLocalProductsRef.current;

        // Step 4: Call prefund — show full left-panel spinner, 3s delay so worker sees total
        setFundingInProgress(true);
        setIsProcessing(true);
        setFundingTotal(fundingAmountWithFudgeFactor.current || '');
        await new Promise(resolve => setTimeout(resolve, 6000));
        try {
          const storeNumber = window.sessionStorage.getItem('storeNumber') || '';

          let fundingProducts: any[];
          let totalFunding: string;

          if ((pricing as any).allResolved && (pricing as any).resolvedProducts) {
            // AC 2: All products resolved (mix of auto-priced + manual) 
            // Use each product's actual pricing response for amounts
            const resolvedProducts: any[] = (pricing as any).resolvedProducts;
            fundingProducts = resolvedProducts.map((rp: any) => ({
              modelNumber: String(rp.modelNumber || ''),
              itemNumber: String(rp.rmsItemNumber || ''),
              amount: String(rp.costPerItem || '0'),
              quantity: String(rp.quantity || '1'),
              weeklyRate: String(rp.weeklyRate || ''),
              weeklyTerm: String(rp.weeklyTerm || ''),
              totalCost: String(rp.totalCost || ''),
              cashPrice: String(rp.cashPrice || ''),
            }));
            // totalFundingAmount = sum of all products' resolved totalCost from pricing
            // totalFunding = String(
            //   resolvedProducts.reduce(
            //     (sum: number, rp: any) => sum + parseFloat(rp.totalCost || '0'),
            //     0
            //   ).toFixed(2)
            // );
          } else {
            // Single pricing resolved
            fundingProducts = currentProducts && currentProducts.length > 0
              ? currentProducts.map((p: any) => ({
                  itemNumber: String(p.rmsInfo?.rmsItemNumber || pricing.rmsItemNumber || ''),
                  modelNumber: String(p.rmsInfo?.modelNumber || ''),
                  amount: String(fundingAmountWithFudgeFactor.current || '0'),
                  quantity: String(p.quantity || '1'),
                  weeklyRate: String(pricing.weeklyRate || ''),
                  weeklyTerm: String((pricing as any).weeklyTerm || ''),
                  totalCost: String(pricing.totalCost || ''),
                  cashPrice: String((pricing as any).cashPrice || ''),
                }))
              : [{
                  itemNumber: String(pricing.rmsItemNumber || ''),
                  modelNumber: String(pricing.rmsItemNumber || ''),
                  amount: String(fundingAmountWithFudgeFactor.current || '0'),
                  quantity: '1',
                  weeklyRate: String(pricing.weeklyRate || ''),
                  weeklyTerm: String((pricing as any).weeklyTerm || ''),
                  totalCost: String(pricing.totalCost || ''),
                  cashPrice: String((pricing as any).cashPrice || ''),
                }];
            // totalFunding = pricing.totalCost;
          }

          totalFunding = fundingAmountWithFudgeFactor.current;
          // Read from router state passed by Trailblazers team
          const locationState: any = (window.history.state as any)?.usr || (window.history.state as any)?.state || {};
          const empId = locationState.employeeId || window.sessionStorage.getItem('employeeId') || '';
          const approvalLvl = locationState.approvalLevel || '1';
          const globalCustId = locationState.globalCustomerId || window.sessionStorage.getItem('globalCustomerId') || '';

          const fundingPayload = {
            totalFundingAmount: `${totalFunding}`,
            storeNumber: storeNumber,
            employeeId: empId,
            approvalLevel: approvalLvl,
            vendorName: currentVendorName,
            globalCustomerId: globalCustId,
            spendOrigin: 'ONLINE',
            products: fundingProducts,
          };
          // Store the payload for potential retry
          lastFundingPayloadRef.current = fundingPayload;
          const fundingResponse = await generateFunding(fundingPayload);
          const fundingData = fundingResponse?.data || fundingResponse;
          const fundingStatus = (fundingData?.status || '').toUpperCase();

          // Compute pricing values for context storage
          const approvedTotal = totalFunding;
          const approvedWeekly = (pricing as any).allResolved && (pricing as any).resolvedProducts
            ? String(
                (pricing as any).resolvedProducts.reduce(
                  (sum: number, rp: any) => sum + parseFloat(rp.weeklyRate || '0'), 0
                ).toFixed(2)
              )
            : pricing.weeklyRate;

          // Build resolved products for NationalPartnerProvider context
          const contextProducts = (pricing as any).allResolved && (pricing as any).resolvedProducts
            ? (pricing as any).resolvedProducts.map((rp: any) => ({
                itemNumber: String(rp.rmsItemNumber || ''),
                weeklyRate: String(rp.weeklyRate || ''),
                weeklyTerm: String(rp.weeklyTerm || ''),
                cashPrice: String(rp.cashPrice || rp.totalCost || ''),
                model: String(rp.model || rp.productName || ''),
                brand: String(rp.brand || ''),
                description: String(rp.productName || rp.model || ''),
                costPerItem: String(rp.costPerItem || rp.unitCost || '0'),
                modelNumber: String(rp.modelNumber || rp.rmsItemNumber || ''),
                quantity: String(rp.quantity || '1'),
              }))
            : [{
                itemNumber: String(pricing.rmsItemNumber || ''),
                weeklyRate: String(pricing.weeklyRate || ''),
                weeklyTerm: String((pricing as any).weeklyTerm || ''),
                cashPrice: String((pricing as any).cashPrice || pricing.totalCost || ''),
                model: String((pricing as any).model || (pricing as any).productName || ''),
                brand: String((pricing as any).brand || ''),
                description: String((pricing as any).productName || (pricing as any).model || (currentProducts && currentProducts.length > 0 ? currentProducts[0].productName : '') || ''),
                costPerItem: String((pricing as any).costPerItem || (pricing as any).unitCost || '0'),
                modelNumber: String(pricing.rmsItemNumber || ''),
                quantity: String((pricing as any).quantity || '1'),
              }];

          // Store pricing/product data in NationalPartnerProvider context
          // so it persists through the flow and is available for navigation
          console.log('[LocalSourcing] contextProducts being stored:', JSON.stringify(contextProducts, null, 2));
          console.log('[LocalSourcing] pricing object received:', JSON.stringify(pricing, null, 2));
          setWeeklyRate(approvedWeekly);
          setTotalCost(approvedTotal);
          setResolvedProducts(contextProducts);
          setStoreNumber(storeNumber);
          setVendorId(vendorCode || null);
          setSupplierName(currentVendorName);
          setSupplierOrderNumber(fundingData?.spendLimitId || '');

          if (fundingStatus === 'APPROVED' || fundingStatus === 'SUCCESS') {
            // AC 3: Show the actual resolved total from all products' pricing
            setFundingState('APPROVED');
            setSpendLimitId(fundingData?.spendLimitId || '');
            setApprovalLimitAmount(fundingData?.approvalLimitAmount || approvedTotal);
            onEligibilityUpdate?.('APPROVED', currentVendorName, approvedTotal, undefined, {
              weeklyRate: approvedWeekly,
              totalCost: approvedTotal,
              spendLimitId: fundingData?.spendLimitId || '',
            });
            setFundingInProgress(false);
            setIsProcessing(false);
          } else if (fundingStatus === 'STARTED' || fundingStatus === 'PENDING') {
            // Funding is pending — Ramp hasn't approved yet. Show as pending and allow retry.
            setFundingInProgress(false);
            setIsProcessing(false);
            setFundingState('TIMED_OUT');
            setDeclineReason('Funding is still being processed by Ramp. Please retry in a few moments.');
          } else {
            setFundingInProgress(false);
            setIsProcessing(false);
            // Extract error message from various backend response formats
            let errorMsg = fundingData?.message || '';
            if (!errorMsg && fundingData?.errors?.length > 0) {
              const err = fundingData.errors[0];
              // Try parsing nested message from stacktrace (backend wraps real error inside)
              if (err.stacktrace) {
                try {
                  const nested = JSON.parse(err.stacktrace);
                  errorMsg = nested?.errors?.[0]?.message || nested?.message || err.error || '';
                } catch { errorMsg = err.error || err.message || ''; }
              } else {
                errorMsg = err.message || err.error || '';
              }
            }
            onEligibilityUpdate?.('DENIED', currentVendorName, totalFunding, errorMsg || 'Prefund request was declined.');
          }
        } catch (err: any) {
          console.error('Generate funding error:', err);
          setFundingInProgress(false);
          setIsProcessing(false);
          // Extract error message from catch (may be axios error with response data)
          let catchMsg = '';
          const errData = err?.response?.data || err?.data;
          if (errData?.errors?.length > 0) {
            const e = errData.errors[0];
            if (e.stacktrace) {
              try { const nested = JSON.parse(e.stacktrace); catchMsg = nested?.errors?.[0]?.message || ''; } catch {}
            }
            if (!catchMsg) catchMsg = e.message || e.error || '';
          }
          if (!catchMsg) catchMsg = err?.message || '';
          if (onEligibilityUpdate) {
            onEligibilityUpdate('DENIED', currentVendorName, pricing.totalCost, catchMsg || 'Unable to process prefund request. Please try again.');
          }
        }
      }} />;
    }

    switch (evaluationStatus) {
      case 'SUCCESS':
        return (
          <div className={classes.successContainer}>
            <Typography className={classes.successTitle}>
              {evaluationResult?.leasable ? 'Item is Leasable' : ' Item is Not Leasable'}
            </Typography>
            <Typography className={classes.successMessage}>
              {evaluationResult?.leasable
                ? 'The captured items have been evaluated and are eligible for lease.'
                : 'The captured items are not eligible for lease. Please review the items and try again.'}
            </Typography>
          </div>
        );

      case 'FAILED':
        return (
          <div className={classes.failedContainer}>
            <Typography className={classes.failedTitle}>
              {'Evaluation Failed'}
            </Typography>
            <Typography className={classes.failedMessage}>
              {evaluationError}
            </Typography>
            <button
              className={classes.retryButton}
              onClick={handleRetry}
            >
              {'Try Again'}
            </button>
          </div>
        );

      case 'TIMEOUT':
        return (
          <div className={classes.failedContainer}>
            <Typography className={classes.failedTitle}>
              {'Request Timed Out'}
            </Typography>
            <Typography className={classes.failedMessage}>
              {evaluationError}
            </Typography>
            <button
              className={classes.retryButton}
              onClick={handleRetry}
            >
              {'Try Again'}
            </button>
          </div>
        );

      case 'IDLE':
      default:
        return (
          <div className={classes.leftPanelPlaceholder}>
            <Typography className={classes.leftPanelText}>
              {'Product details will appear here'}
            </Typography>
          </div>
        );
    }
  };

  return (
    <div className={clsx(classes.capturedImageRoot)}>
      <RACCard className={classes.card}>
        <div className={clsx(classes.cardBody)}>
          <div className={classes.contentRow}>
            {/* Left panel - Status / Evaluation */}
            <div className={classes.leftPanel}>
              {/* Full left-panel funding overlay — outside scroll, covers entire panel */}
              {fundingInProgress && (
                <div style={{
                  position: 'absolute' as const,
                  top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: '#FAFAFA',
                  zIndex: 20,
                  display: 'flex',
                  flexDirection: 'column' as const,
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1rem',
                  borderRadius: '8px',
                }}>
                  <ProcessSpinner className={classes.spinnerIcon} />
                  <Typography className={classes.statusMessage}>
                    {'Securing funding on Ramp card...'}
                  </Typography>
                  {fundingTotal && (
                    <Typography style={{ fontSize: '1rem', fontWeight: 700, color: '#111' }}>
                      {`Total Financing Request: $${fundingTotal}`}
                    </Typography>
                  )}
                  <Typography style={{ fontSize: '0.75rem', color: '#888', textAlign: 'center' as const }}>
                    {'Please do not close this screen.'}
                  </Typography>
                </div>
              )}
              {/* Funding error overlay — TIMED_OUT, DECLINED, CONNECTION_ERROR */}
              {(fundingState === 'TIMED_OUT' || fundingState === 'DECLINED' || fundingState === 'CONNECTION_ERROR') && !fundingInProgress && (
                <div style={{
                  position: 'absolute' as const,
                  top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: '#FAFAFA',
                  zIndex: 20,
                  display: 'flex',
                  flexDirection: 'column' as const,
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1rem',
                  borderRadius: '8px',
                  padding: '1.5rem',
                }}>
                  <Typography style={{ fontSize: '1.125rem', fontWeight: 700, color: '#C62828', textAlign: 'center' as const }}>
                    {fundingState === 'TIMED_OUT' && 'Funding Timed Out'}
                    {fundingState === 'DECLINED' && 'Funding Declined'}
                    {fundingState === 'CONNECTION_ERROR' && 'Connection Error'}
                  </Typography>
                  <Typography style={{ fontSize: '0.875rem', color: '#333', textAlign: 'center' as const, lineHeight: 1.5 }}>
                    {fundingState === 'TIMED_OUT' && 'Funding could not be confirmed within the expected timeframe.'}
                    {fundingState === 'DECLINED' && (declineReason || 'Funding request was declined.')}
                    {fundingState === 'CONNECTION_ERROR' && 'Connection error. Unable to reach the funding service.'}
                  </Typography>
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap' as const, justifyContent: 'center' }}>
                    <button
                      onClick={handleRetryFunding}
                      style={{
                        padding: '8px 24px',
                        backgroundColor: '#2196F3',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontFamily: 'OpenSans-semibold',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Retry Funding
                    </button>
                    <button
                      onClick={handleStartOver}
                      style={{
                        padding: '8px 24px',
                        backgroundColor: 'transparent',
                        color: '#2196F3',
                        border: '1px solid #2196F3',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontFamily: 'OpenSans-semibold',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Start Over
                    </button>
                    <button
                      onClick={handleCancel}
                      style={{
                        padding: '8px 24px',
                        backgroundColor: 'transparent',
                        color: '#666',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontFamily: 'OpenSans-semibold',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {renderLeftPanel()}
              {/* Checkout flow state rendering — takes priority over evaluation states */}
              {fundingState === 'APPROVED' && checkoutState === 'NONE' && (
                <div style={{
                  position: 'absolute' as const,
                  top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: '#FAFAFA',
                  zIndex: 15,
                  borderRadius: '8px',
                }}>
                  <FundingApprovedPanel
                    approvalLimitAmount={approvalLimitAmount}
                    weeklyRate={weeklyRate}
                    totalCost={totalCost}
                    vendorName={shopLocalVendorName}
                  />
                </div>
              )}
              {checkoutState === 'IN_PROGRESS' && (
                <div style={{
                  position: 'absolute' as const,
                  top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: '#FAFAFA',
                  zIndex: 15,
                  display: 'flex',
                  flexDirection: 'column' as const,
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1rem',
                  borderRadius: '8px',
                  padding: '1.5rem',
                }}>
                  <CircularProgress size={40} />
                  <Typography style={{ fontSize: '1rem', fontWeight: 600, color: '#333', textAlign: 'center' as const }}>
                    {`Please complete your checkout on ${shopLocalVendorName}`}
                  </Typography>
                  <Typography style={{ fontSize: '0.8rem', color: '#666', textAlign: 'center' as const }}>
                    {'Enter your Ramp card details on the retailer site to complete the purchase.'}
                  </Typography>
                </div>
              )}
              {checkoutState === 'VERIFYING_PURCHASE' && (
                <div style={{
                  position: 'absolute' as const,
                  top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: '#FAFAFA',
                  zIndex: 15,
                  display: 'flex',
                  flexDirection: 'column' as const,
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1rem',
                  borderRadius: '8px',
                  padding: '1.5rem',
                }}>
                  <CircularProgress size={40} />
                  <Typography style={{ fontSize: '1rem', fontWeight: 600, color: '#333', textAlign: 'center' as const }}>
                    {checkoutMessage}
                  </Typography>
                  <Typography style={{ fontSize: '0.8rem', color: '#666', textAlign: 'center' as const }}>
                    {'Confirming that the payment was authorized. Please wait.'}
                  </Typography>
                </div>
              )}
              {checkoutState === 'PURCHASE_SUCCESSFUL' && (
                <div style={{
                  position: 'absolute' as const,
                  top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: '#FAFAFA',
                  zIndex: 15,
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column' as const,
                }}>
                  <PostCheckoutConfirmation
                    approvalLimitAmount={transactionAmount || approvalLimitAmount}
                  />
                </div>
              )}
              {checkoutState === 'CHECKOUT_FAILED' && (
                <div style={{
                  position: 'absolute' as const,
                  top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: '#FAFAFA',
                  zIndex: 20,
                  display: 'flex',
                  flexDirection: 'column' as const,
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  borderRadius: '8px',
                  padding: '1.5rem',
                }}>
                  <Typography style={{ fontSize: '0.85rem', fontWeight: 700, color: '#C62828', textAlign: 'center' as const }}>
                    Checkout Could Not Be Verified
                  </Typography>
                  <Typography style={{ fontSize: '0.7rem', color: '#333', textAlign: 'center' as const, lineHeight: 1.5 }}>
                    {'We are unable to verify the payment completion at this time. If the purchase was completed successfully, it will appear on the Dashboard once authorized.'}
                  </Typography>
                  <Typography style={{ fontSize: '0.7rem', color: '#333', textAlign: 'center' as const, lineHeight: 1.5 }}>
                    {'You can check the dashboard\'s pending actions under the Local Sourcing tab to complete the agreement after confirmation is obtained.'}
                  </Typography>
                  <Typography style={{ fontSize: '0.7rem', color: '#333', textAlign: 'center' as const, lineHeight: 1.5 }}>
                    {'Otherwise, if you need assistance, please contact support and provide the following reference:'}
                  </Typography>
                  <Typography style={{ fontSize: '0.7rem', fontWeight: 700, color: '#333', textAlign: 'center' as const, lineHeight: 1.5, backgroundColor: '#F5F5F5', padding: '0.4rem 0.75rem', borderRadius: '4px', wordBreak: 'break-all' as const }}>
                    {`Reference ID: ${spendLimitId || 'N/A'}`}
                  </Typography>
                </div>
              )}
            </div>

            {/* Right panel - captured screenshot with scroll */}
            <div
              className={clsx(
                classes.rightPanel,
                !(capturedImageUrl && isValidCapture) && classes.rightPanelEmpty
              )}
              style={{ position: 'relative' }}
            >
              {capturedImageUrl && isValidCapture ? (
                <div className={classes.imageScrollContainer}>
                  <img
                    ref={imageRef}
                    src={capturedImageUrl}
                    alt="Captured product"
                    className={classes.capturedImg}
                  />
                </div>
              ) : (
                <>
                  {altTPressedRef.current && (
                    <button
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        padding: '6px 16px',
                        backgroundColor: 'transparent',
                        color: '#666',
                        border: '1.5px solid #2196f3',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontFamily: 'OpenSans-semibold',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textDecoration: 'none',
                      }}
                    >
                      Get Image
                    </button>
                  )}
                  <div className={classes.placeholder}>
                    <Typography className={classes.placeholderText}>
                      {'No image captured'}
                    </Typography>
                    <Typography className={classes.placeholderHint}>
                      {'Use Alt+T on retailer window'}
                    </Typography>
                  </div>
                </>
              )}
            </div>
          </div>

          {error && (
            <Typography
              style={{ color: 'red', fontSize: '0.75rem', marginTop: '0.5rem' }}
            >
              {error}
            </Typography>
          )}
        </div>
      </RACCard>
    </div>
  );
};